/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { FileText } from 'lucide-react';
import Seo from '../components/Seo';

const TermsPage = () => {
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <Seo
        title="Terms of Service – MyCBC"
        description="Terms of service for MyCBC, the CLL/SLL blood cancer companion app."
        path="/terms"
      />
      <div className="flex items-center gap-3 mb-6">
        <FileText className="h-8 w-8 text-primary" />
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900">Terms of Service</h1>
      </div>

      <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm space-y-6">
        <div className="prose prose-slate max-w-none">
          <p className="text-sm text-slate-500">Last Updated: December 2025</p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">1. Acceptance of Terms</h2>
          <p className="text-slate-700 leading-relaxed">
            By accessing and using Blood Cancer Companion ("the App"), you accept and agree to be bound by these Terms of Service. 
            If you do not agree to these terms, please do not use the App.
          </p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">2. Description of Service</h2>
          <p className="text-slate-700 leading-relaxed">
            Blood Cancer Companion is a personal health tracking application that allows users to record and monitor their own 
            health information, including blood test results, symptoms, and medications. The App provides tools for data visualization, 
            trend analysis, and report generation.
          </p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">3. User-Entered Data and Self-Management</h2>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 my-4">
            <p className="text-slate-900 font-semibold mb-2">Important: Data Responsibility</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li><strong>All data in the App is user-entered and self-managed.</strong></li>
              <li>You are solely responsible for the accuracy and completeness of all information you enter.</li>
              <li>The App does not verify, validate, or fact-check any data you input.</li>
              <li>We do not access, review, or modify your health data.</li>
              <li>You are responsible for keeping your data current and accurate.</li>
            </ul>
          </div>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">4. Not Medical Advice or Clinical Tool</h2>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 my-4">
            <p className="text-slate-900 font-semibold mb-2">Medical Disclaimer</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li><strong>This App is NOT a medical device, medical record system, or clinical decision-making tool.</strong></li>
              <li>The App does not provide medical advice, diagnosis, or treatment recommendations.</li>
              <li>Information provided by the App (including alerts and trend analysis) is for informational and tracking purposes only.</li>
              <li>The App is not intended to replace professional medical advice or consultation with qualified healthcare providers.</li>
              <li>Always consult your doctor or healthcare provider before making any medical decisions.</li>
              <li>In case of a medical emergency, call emergency services immediately. Do not rely on this App.</li>
            </ul>
          </div>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">5. Alerts and Notifications</h2>
          <p className="text-slate-700 leading-relaxed">
            The App may generate alerts based on the data you enter and predefined clinical reference ranges. These alerts are 
            automated notifications based on statistical patterns and are <strong>not medical diagnoses or professional medical opinions</strong>. 
            Alerts should be used as prompts to discuss findings with your healthcare provider, not as standalone medical guidance.
          </p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">6. PDF Exports and Reports</h2>
          <p className="text-slate-700 leading-relaxed">
            PDF reports generated by the App contain user-entered information for personal reference and to facilitate communication 
            with healthcare providers. These reports are <strong>not official medical records</strong> and should not be used as a substitute 
            for professional medical documentation or clinical records maintained by healthcare facilities.
          </p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">7. User Responsibilities</h2>
          <p className="text-slate-700 leading-relaxed">You agree to:</p>
          <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
            <li>Use the App for personal health tracking purposes only</li>
            <li>Enter accurate and truthful information to the best of your ability</li>
            <li>Maintain the confidentiality of your account credentials</li>
            <li>Not use the App as a replacement for professional medical care</li>
            <li>Consult qualified healthcare professionals for all medical decisions</li>
            <li>Not rely solely on the App for monitoring critical health conditions</li>
          </ul>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">8. Data Privacy and Security</h2>
          <p className="text-slate-700 leading-relaxed">
            We take your privacy seriously. Please review our <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a> to 
            understand how we collect, use, and protect your personal health information.
          </p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">9. Limitation of Liability</h2>
          <p className="text-slate-700 leading-relaxed">
            To the fullest extent permitted by law, Blood Cancer Companion, its developers, and operators are not liable for any 
            damages arising from your use of the App, including but not limited to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
            <li>Medical decisions made based on App data or alerts</li>
            <li>Errors or inaccuracies in user-entered data</li>
            <li>Technical failures or data loss</li>
            <li>Delayed or missed medical diagnoses</li>
            <li>Any health outcomes related to App usage</li>
          </ul>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">10. No Warranty</h2>
          <p className="text-slate-700 leading-relaxed">
            The App is provided "as is" without warranties of any kind, either express or implied. We do not warrant that the 
            App will be uninterrupted, error-free, or free from viruses or other harmful components.
          </p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">11. Changes to Terms</h2>
          <p className="text-slate-700 leading-relaxed">
            We reserve the right to modify these Terms of Service at any time. Continued use of the App after changes constitutes 
            acceptance of the modified terms.
          </p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">12. Termination</h2>
          <p className="text-slate-700 leading-relaxed">
            We reserve the right to suspend or terminate your access to the App at any time, with or without notice, for any reason.
          </p>

          <h2 className="text-2xl font-semibold text-slate-900 mt-8 mb-4">13. Contact Information</h2>
          <p className="text-slate-700 leading-relaxed">
            If you have questions about these Terms of Service, please contact us through the App's support channels.
          </p>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-8">
            <p className="text-slate-700 text-sm">
              <strong>Summary:</strong> Blood Cancer Companion is a personal health tracking tool for informational purposes only. 
              All data is user-entered and self-managed. This App is not a medical device and does not provide medical advice. 
              Always consult qualified healthcare professionals for medical guidance and decisions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
